import classNames from "classnames";
import React, { FC, ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";
import { LuEye, LuLock, LuUpload, LuX } from "react-icons/lu";

import { FolderVisibility } from "../files.types";
import { fileBadge } from "../files.utils";

// Shared look for the Documents screens (V49: purple #6B4EFF, Manrope, soft borders).

export const PRIMARY_BUTTON =
  "inline-flex h-9 items-center justify-center gap-2 rounded-[10px] bg-[#6B4EFF] px-4 text-[13px] font-bold text-white shadow-[0_3px_10px_rgba(107,78,255,0.25)] transition-colors hover:bg-[#5A3EEB] disabled:cursor-not-allowed disabled:bg-[#C3C8D4] disabled:shadow-none";

export const SECONDARY_BUTTON =
  "inline-flex h-9 items-center justify-center gap-2 rounded-[10px] border border-[#DCE2EA] bg-white px-3.5 text-[13px] font-semibold text-[#1F2433] transition-colors hover:bg-[#F7F8FA] disabled:cursor-not-allowed disabled:opacity-60";

export const GHOST_BUTTON =
  "inline-flex h-9 items-center justify-center gap-2 rounded-[10px] px-3 text-[13px] font-semibold text-[#5A3EEB] transition-colors hover:bg-[#F4F2FF] disabled:opacity-50";

export const DANGER_BUTTON =
  "inline-flex h-9 items-center justify-center gap-2 rounded-[10px] bg-[#E5484D] px-4 text-[13px] font-bold text-white transition-colors hover:bg-[#D13A3F] disabled:cursor-not-allowed disabled:opacity-60";

export const INPUT =
  "h-9 w-full rounded-[10px] border border-[#DCE2EA] bg-white px-3 text-[13px] text-[#1F2433] outline-none placeholder:text-[#9AA0B0] focus:border-[#6B4EFF]";

/** A dropdown sized to its text (filters). */
export const SELECT =
  "h-9 w-auto max-w-full rounded-[10px] border border-[#DCE2EA] bg-white pl-3 pr-8 text-[13px] text-[#1F2433] outline-none focus:border-[#6B4EFF]";

export const LABEL = "mb-1.5 block text-xs font-bold text-[#4A5168]";

export const CARD = "rounded-2xl border border-[#E6E9EF] bg-white";

/** The colored file-type tile (PDF red, Excel green...). */
export const FileTypeIcon: FC<{ name: string; size?: number }> = ({
  name,
  size = 30,
}) => {
  const badge = fileBadge(name);
  return (
    <span
      aria-hidden={true}
      style={{ width: size * 0.88, height: size, background: badge.color }}
      className={
        "inline-flex flex-none items-center justify-center rounded-md text-[9px] font-extrabold tracking-wide text-white"
      }>
      {badge.label}
    </span>
  );
};

export const FolderGlyph: FC<{ size?: number; className?: string }> = ({
  size = 18,
  className,
}) => (
  <svg
    width={size}
    height={size}
    viewBox={"0 0 24 24"}
    className={classNames("flex-none", className)}
    aria-hidden={true}>
    <path
      fill={"currentColor"}
      d={
        "M3 6.5A2.5 2.5 0 0 1 5.5 4h4.2c.6 0 1.2.3 1.6.8L12.6 6.5h5.9A2.5 2.5 0 0 1 21 9v8.5a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 17.5z"
      }
    />
  </svg>
);

/** "Firm only" / "Client can see" / "Client can add files". */
export const VisibilityPill: FC<{
  visibility: Exclude<FolderVisibility, "inherit">;
  compact?: boolean;
}> = ({ visibility, compact }) => {
  if (visibility === "private") {
    return (
      <span
        className={
          "inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-[#DCE2EA] bg-[#F7F8FA] px-2 py-0.5 text-[11px] font-bold text-[#667085]"
        }>
        <LuLock size={11} />
        {compact ? "Firm only" : "Firm only"}
      </span>
    );
  }
  return (
    <span
      className={
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-[#E5F0FD] px-2 py-0.5 text-[11px] font-bold text-[#1F6FD1]"
      }>
      {visibility === "uploads" ? <LuUpload size={11} /> : <LuEye size={11} />}
      {visibility === "uploads"
        ? compact
          ? "Client can add"
          : "Client can see and add"
        : "Client can see"}
    </span>
  );
};

export const Pill: FC<{
  tone: "new" | "client" | "ok" | "muted" | "shared";
  children: ReactNode;
}> = ({ tone, children }) => (
  <span
    className={classNames(
      "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold",
      tone === "new" && "bg-[#6B4EFF] text-white",
      tone === "client" && "bg-[#FFF2DC] text-[#A15C00]",
      tone === "ok" && "bg-[#E3F6EE] text-[#0E8A5F]",
      tone === "shared" && "bg-[#E5F0FD] text-[#1F6FD1]",
      tone === "muted" && "border border-[#DCE2EA] bg-[#F7F8FA] text-[#667085]",
    )}>
    {children}
  </span>
);

/** A window over the page (portal, Esc / backdrop closes unless busy). */
export const FilesModal: FC<{
  title: string;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
  busy?: boolean;
}> = ({ title, subtitle, onClose, children, footer, width = 480, busy }) => {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  return createPortal(
    <div
      className={
        "fixed inset-0 z-[60] flex items-center justify-center bg-[#1F2433]/30 p-4 font-Manrope"
      }
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}>
      <div
        role={"dialog"}
        aria-modal={true}
        aria-label={title}
        style={{ maxWidth: width }}
        className={
          "flex max-h-[90vh] w-full flex-col overflow-hidden rounded-2xl bg-white shadow-[0_24px_60px_rgba(20,22,34,0.25)]"
        }>
        <div
          className={
            "flex items-start justify-between gap-3 border-b border-[#EDEFF3] px-5 py-4"
          }>
          <div className={"min-w-0"}>
            <div className={"text-base font-extrabold text-[#1F2433]"}>
              {title}
            </div>
            {subtitle && (
              <div className={"mt-0.5 truncate text-[13px] text-[#667085]"}>
                {subtitle}
              </div>
            )}
          </div>
          <button
            type={"button"}
            aria-label={"Close"}
            disabled={busy}
            onClick={onClose}
            className={
              "flex h-8 w-8 flex-none items-center justify-center rounded-lg text-[#667085] hover:bg-[#F4F2FF]"
            }>
            <LuX size={18} />
          </button>
        </div>
        <div
          className={
            "min-h-0 flex-1 overflow-y-auto px-5 py-4 text-[13px] text-[#1F2433]"
          }>
          {children}
        </div>
        {footer && (
          <div
            className={
              "flex justify-end gap-2 border-t border-[#EDEFF3] px-5 py-3"
            }>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

/** On/off switch with a label and a short explanation. */
export const Toggle: FC<{
  id: string;
  checked: boolean;
  disabled?: boolean;
  label: string;
  hint?: string;
  onChange: (checked: boolean) => void;
}> = ({ id, checked, disabled, label, hint, onChange }) => (
  <label
    htmlFor={id}
    className={classNames(
      "flex items-start gap-3",
      disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
    )}>
    <button
      id={id}
      type={"button"}
      role={"switch"}
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={classNames(
        "relative mt-0.5 h-[18px] w-8 flex-none rounded-full transition-colors",
        checked ? "bg-[#6B4EFF]" : "bg-[#DCE2EA]",
      )}>
      <span
        className={classNames(
          "absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white transition-all",
          checked ? "left-[16px]" : "left-[2px]",
        )}
      />
    </button>
    <span>
      <span className={"block text-[13px] font-bold text-[#1F2433]"}>
        {label}
      </span>
      {hint && <span className={"block text-xs text-[#667085]"}>{hint}</span>}
    </span>
  </label>
);

/** Red text under a form. */
export const ErrorText: FC<{ children?: ReactNode }> = ({ children }) =>
  children ? (
    <div
      role={"alert"}
      className={
        "mt-3 rounded-lg bg-[#FDE8E8] px-3 py-2 text-[13px] font-semibold text-[#C2353A]"
      }>
      {children}
    </div>
  ) : null;

/** The error text from an RTK Query error (the API answers plain text). */
export const errorText = (
  error: unknown,
  fallback = "Something went wrong. Please try again.",
) => {
  const data = (error as { data?: unknown })?.data;
  if (typeof data === "string" && data.trim() && data.length < 400) return data;
  if (error instanceof Error && error.message && error.message.length < 400)
    return error.message;
  return fallback;
};
